import { useEffect, useState } from "react";
import { Input } from "antd";
import { useAllOrders } from "../../../../../api/orders/orders";
import { useLoader } from "../../../../../utils/LoaderContext";

const ORDERS_PER_PAGE = 20;

const Delivered = ({ status = "delivered" }) => {
  const { setLoading } = useLoader();

  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState("");

  // -----------------------
  // 🔹 Debounce Search
  // -----------------------
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText.trim());
    }, 400);

    return () => clearTimeout(timer);
  }, [searchText]);

  // 🔹 Reset page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchText]);

  // -----------------------
  // 🔹 SINGLE API CALL
  // -----------------------
  const {
    data: allOrders = { orders: [], totalPages: 1 },
    isLoading,
    isFetching,
    isError,
  } = useAllOrders(
    {
      currentPage,
      limit: ORDERS_PER_PAGE,
      status,
      search: debouncedSearchText, // ✅ search stays here
    },
    {
      keepPreviousData: true,
    }
  );

  // 🔹 Global loader sync
  useEffect(() => {
    setLoading(isLoading || isFetching);
  }, [isLoading, isFetching, setLoading]);

  if (isError) {
    return (
      <p className="text-red-600 mt-4">Error fetching delivered orders.</p>
    );
  }

  const orders = allOrders.orders || [];
  const totalPages = allOrders.totalPages || 1;

  return (
    <div className="w-full mt-6">
      {/* ---------------- Search ---------------- */}
      <div className="mb-4 flex justify-between items-center">
        <Input
          className="w-80"
          placeholder="Search by AWB, Order Number or Name"
          value={searchText}
          allowClear
          onChange={(e) => setSearchText(e.target.value)}
        />
      </div>

      {/* ---------------- Table ---------------- */}
      <div className="overflow-x-auto hide-scrollbar">
        <table className="min-w-[1000px] w-full table-auto border border-gray-200">
          <thead className="bg-gray-100 text-left text-sm font-semibold text-gray-700">
            <tr>
              <th className="p-3">Order Date</th>
              <th className="p-3">AWB / Status</th>
              <th className="p-3">Payment</th>
              <th className="p-3">Products</th>
              <th className="p-3">Customer</th>
              <th className="p-3">Address</th>
            </tr>
          </thead>

          <tbody className="bg-white text-sm">
            {isLoading ? (
              <tr>
                <td colSpan={6} className="text-center py-6 text-gray-500">
                  Loading delivered orders...
                </td>
              </tr>
            ) : orders.length > 0 ? (
              orders.map((order, index) => (
                <tr key={order._id || index} className="hover:bg-gray-50">
                  <td className="p-4">
                    {new Date(order.createdAt).toLocaleDateString()}
                  </td>

                  <td className="p-4">
                    <div className="font-medium text-blue-600">
                      {order.awbNumber}
                    </div>
                    <span className="inline-block mt-1 text-xs font-semibold bg-purple-100 text-purple-600 px-2 py-1 rounded">
                      {order.shippingStatusText}
                    </span>
                  </td>

                  <td className="p-4">
                    <p>₹{order.InvoiceAmount}</p>
                    <span className="inline-block mt-1 text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                      {order.PaymentType}
                    </span>
                  </td>

                  <td className="p-4">
                    {order.ProductDetails?.map((prod, i) => (
                      <p key={i} className="truncate">
                        {prod.name}
                      </p>
                    ))}
                  </td>

                  <td className="p-4">
                    <p>{order.CustomerName}</p>
                    <p className="text-xs text-gray-500">
                      {order.BillingAddress?.Contact}
                    </p>
                  </td>

                  <td className="p-4 text-sm">
                    <p>{order.BillingAddress?.City}</p>
                    <p>{order.BillingAddress?.State}</p>
                    <p>{order.BillingAddress?.Pincode}</p>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6} className="text-center py-6 text-gray-400">
                  No delivered orders found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ---------------- Pagination ---------------- */}
      <div className="flex justify-between items-center mt-6 text-sm text-gray-700">
        <p>
          Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
          {isFetching && (
            <span className="ml-3 text-blue-500 animate-pulse">Loading...</span>
          )}
        </p>

        <div className="space-x-2">
          <button
            onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
            disabled={currentPage === 1 || isFetching}
            className={`px-4 py-2 rounded border ${
              currentPage === 1 || isFetching
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-white hover:bg-gray-50"
            }`}
          >
            Previous
          </button>

          <button
            onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
            disabled={currentPage === totalPages || isFetching}
            className={`px-4 py-2 rounded border ${
              currentPage === totalPages || isFetching
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-white hover:bg-gray-50"
            }`}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default Delivered;
